import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import client from '@api/client';
import { AdminUser } from '@api/subClients/AdminClient';
import { Page } from '../../Layouts/Page/Page';

export const AdminUsers: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = React.useState<AdminUser[] | null>(null);
  const [userPendingDelete, setUserPendingDelete] = React.useState<AdminUser | null>(null);
  const [newUserEmail, setNewUserEmail] = React.useState('');
  const [isCreatingUser, setIsCreatingUser] = React.useState(false);

  const loadUsers = React.useCallback(async () => {
    try {
      setUsers(await client.admin.listUsers());
    } catch (err) {
      toast.error(t('pages.adminUsers.loadErrorMsg') as string);
    }
  }, [t]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const updateField = async (user: AdminUser, data: { email?: string; displayName?: string }) => {
    const previousUsers = users;
    setUsers((current) => current?.map((u) => (u.actorId === user.actorId ? { ...u, ...data } : u)) ?? current);
    try {
      await client.admin.updateUser(user.actorId, data);
    } catch (err) {
      toast.error(t('pages.adminUsers.updateErrorMsg') as string);
      setUsers(previousUsers);
    }
  };

  const createUser = async () => {
    const email = newUserEmail.trim();
    if (!email) return;
    setIsCreatingUser(true);
    try {
      await client.admin.createUser(email);
      toast.success(t('pages.adminUsers.createSuccessMsg') as string);
      setNewUserEmail('');
      await loadUsers();
    } catch (err) {
      toast.error(t('pages.adminUsers.createErrorMsg') as string);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const confirmDelete = async () => {
    if (!userPendingDelete) return;
    try {
      await client.admin.deleteUser(userPendingDelete.actorId);
      setUsers((current) => current?.filter((u) => u.actorId !== userPendingDelete.actorId) ?? current);
      toast.success(t('pages.adminUsers.deleteSuccessMsg') as string);
    } catch (err) {
      toast.error(t('pages.adminUsers.deleteErrorMsg') as string);
    } finally {
      setUserPendingDelete(null);
    }
  };

  return (
    <Page pageTitle={t('pages.adminUsers.titleText') as string}>
      <Box p={4}>
        <Typography variant="h5" gutterBottom>
          {t('pages.adminUsers.titleText')}
        </Typography>
        <Box display="flex" alignItems="flex-start" mb={3}>
          <TextField
            placeholder={t('pages.adminUsers.newUser.emailPlaceholder') as string}
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') createUser();
            }}
          />
          <Box ml={2}>
            <Button variant="contained" color="primary" disabled={!newUserEmail.trim() || isCreatingUser} onClick={createUser}>
              {t('pages.adminUsers.newUser.submitButtonText')}
            </Button>
          </Box>
        </Box>
        {users === null ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('pages.adminUsers.columns.email')}</TableCell>
                <TableCell>{t('pages.adminUsers.columns.displayName')}</TableCell>
                <TableCell>{t('pages.adminUsers.columns.createdAt')}</TableCell>
                <TableCell align="right">{t('pages.adminUsers.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.actorId}>
                  <TableCell>
                    <TextField
                      defaultValue={user.email ?? ''}
                      onBlur={(e) => {
                        if (e.target.value !== (user.email ?? '')) {
                          updateField(user, { email: e.target.value });
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      defaultValue={user.displayName ?? ''}
                      onBlur={(e) => {
                        if (e.target.value !== (user.displayName ?? '')) {
                          updateField(user, { displayName: e.target.value });
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton aria-label="delete" onClick={() => setUserPendingDelete(user)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      <Dialog open={!!userPendingDelete} onClose={() => setUserPendingDelete(null)}>
        <DialogTitle>{t('pages.adminUsers.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('pages.adminUsers.deleteDialog.body', { email: userPendingDelete?.email })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserPendingDelete(null)}>{t('pages.adminUsers.deleteDialog.cancel')}</Button>
          <Button color="secondary" onClick={confirmDelete}>
            {t('pages.adminUsers.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default AdminUsers;
